/**
 * Shared Read Excel Tool - Schema
 *
 * Supports both session mode (LinkedExcelEditorAgent) and filepath mode (ExternalFileSearchAgent).
 */
import { z } from "zod";
// =============================================================================
// CONTENT MODES
// =============================================================================
/** Content read modes */
export const ContentModeSchema = z.enum([
    "structure", // Sheet only - layout discovery
    "structureWithFormulaPatterns", // Formula patterns (alias: "regular")
    "structureWithFormulaPatternsAndValues", // Patterns + values (alias: "regular_and_values")
    "detailedValues", // All values, range required (alias: "values")
    "detailedValuesAndFormulas", // Values + formulas, range required (alias: "values_and_formulas")
]);
/** Modes that only work on full sheets */
export const SHEET_ONLY_MODES = ["structure"];
/** Modes where targetRange is required */
export const RANGE_REQUIRED_MODES = [
    "detailedValues",
    "detailedValuesAndFormulas",
];
// =============================================================================
// FILE REFERENCE (for filepath mode)
// =============================================================================
/**
 * File reference - can be a numeric index or "CURRENT" for current workbook
 */
export const FileIndexSchema = z.union([
    z.number().int().nonnegative(),
    z.literal("CURRENT"),
]).describe("File index (0, 1, 2...) or 'CURRENT' for current workbook");
// =============================================================================
// OPERATION SCHEMAS
// =============================================================================
/** Single content read operation */
export const ReadContentItemSchema = z.object({
    // File reference (filepath mode only)
    fileIndex: FileIndexSchema.optional().describe("File index or 'CURRENT'. Only used in filepath mode."),
    sheetName: z.string().describe("Name of the sheet to read."),
    targetRange: z.string().optional().describe(`
Excel range to read (e.g., "A1:D10", "B2:Z100").

**Required for:** detailedValues, detailedValuesAndFormulas
**Optional for:** structure (ignored), structureWithFormulaPatterns, structureWithFormulaPatternsAndValues
`),
    mode: ContentModeSchema.describe(`
Read mode - determines what data is returned:

**structure** (sheet only) - Layout discovery, text/dates only. NO CELL LIMITS. Best for whole sheets.
**structureWithFormulaPatterns** - Normalized formula patterns like ={col-1}+{col-2}. NO CELL LIMITS.
**structureWithFormulaPatternsAndValues** - Patterns AND actual values. Good for large ranges.
**detailedValues** - All calculated values. Range REQUIRED.
**detailedValuesAndFormulas** - Values + raw formulas. Range REQUIRED.
`),
});
/** Single formatting read operation */
export const ReadFormattingItemSchema = z.object({
    fileIndex: FileIndexSchema.optional().describe("File index or 'CURRENT'. Only used in filepath mode."),
    sheetName: z.string().describe("Name of the sheet to read formatting from."),
    targetRange: z.string().describe("Excel range to read formatting from (e.g., 'A1:F20'). REQUIRED for formatting reads."),
    includeContent: z.boolean().optional().default(true).describe("Include underlying cell content (values + formula patterns). Default: true."),
});
/** Chart discovery operation. */
export const ListChartsItemSchema = z.object({
    fileIndex: FileIndexSchema.optional().describe("File index or 'CURRENT'. Only used in filepath mode."),
    sheetName: z.string().describe("Sheet name to list charts from."),
});
/** Canonical chart read operation (can target one chart or all charts on a sheet). */
export const ReadChartsItemSchema = z.object({
    fileIndex: FileIndexSchema.optional().describe("File index or 'CURRENT'. Only used in filepath mode."),
    sheetName: z.string().describe("Sheet name containing the target chart(s)."),
    chartId: z.string().optional().describe("Optional chart ID/name locator."),
    chartName: z.string().optional().describe("Optional chart name locator (alias for chartId)."),
    dataMode: z.enum(["auto", "summary-style", "point-level"]).optional().default("auto").describe("Chart read granularity. 'auto' lets the runtime choose."),
});
/** Single formula trace operation */
export const FormulaTraceItemSchema = z.object({
    fileIndex: FileIndexSchema.optional().describe("File index or 'CURRENT'. Only used in filepath mode."),
    sheetName: z.string().describe("Name of the sheet containing the cell to trace."),
    cellAddress: z.string().describe(`
Single cell address to trace (e.g., "B5", "AA100").

Must be a single cell, NOT a range. The cell should contain a formula for meaningful results.
`),
    direction: z.enum(["precedents", "dependents", "both"]).optional().default("both").describe(`
**precedents** - Cells that this formula references (inputs)
**dependents** - Cells that reference this cell (outputs)
**both** - Trace in both directions (default)
`),
    recursive: z.boolean().optional().default(true).describe("Trace recursively through the dependency chain. Default: true."),
    maxDepth: z.number().int().min(1).max(50).optional().default(10).describe("Maximum recursion depth. Default: 10, Max: 50."),
});
/** Single table analysis operation */
export const TableAnalysisItemSchema = z.object({
    fileIndex: FileIndexSchema.optional().describe("File index or 'CURRENT'. Only used in filepath mode."),
    sheetName: z.string().describe("Name of the sheet containing the table data."),
    targetRange: z.string().describe("Excel range containing the table to analyze (e.g., 'A1:F100'). REQUIRED."),
    groupBy: z.enum(["column", "row"]).optional().default("column").describe(`
How to slice data for analysis:

**column** (default) - Analyze each column as a data series. Best for standard tables.
**row** - Analyze each row as a data series. Best for transposed/time series data.
`),
    valueThreshold: z.number().min(0).max(1).optional().default(0.05).describe("Threshold for reporting value distributions (0-1). Default: 0.05 (5%)."),
    includeEmptyAnalysis: z.boolean().optional().default(true).describe("Include analysis of empty cell patterns. Default: true."),
});
/** Single list sheets operation */
export const ListSheetsItemSchema = z.object({
    fileIndex: FileIndexSchema.optional().describe("File index or 'CURRENT'. Only used in filepath mode."),
});
// =============================================================================
// UNIFIED READ EXCEL SCHEMA
// =============================================================================
export const ReadExcelParamsSchema = z.object({
    uiDescription: z.string()
        .describe("Brief description (max ~15 words) of what this tool call is attempting."),
    readContent: z.array(ReadContentItemSchema).optional().describe(`
Read cell content from one or more ranges/sheets.

RECOMMENDED FLOW:
1. Start with mode='structure' on whole sheets to understand layout
2. Use mode='structureWithFormulaPatterns' for formula analysis
3. Use mode='detailedValues' or mode='detailedValuesAndFormulas' for targeted deep inspection

Example:
[
  { "sheetName": "Dashboard", "mode": "structure" },
  { "sheetName": "Data", "targetRange": "A1:D10", "mode": "detailedValues" }
]
`),
    readFormatting: z.array(ReadFormattingItemSchema).optional().describe(`
Read cell formatting (fonts, colors, borders, alignment) as grouped sharedFormatRanges.

Always requires targetRange - formatting reads can be expensive.
Automatically includes underlying content for context.

Example:
[
  { "sheetName": "Report", "targetRange": "A1:F20" }
]
`),
    readCharts: z.array(ReadChartsItemSchema).optional().describe(`
Read chart payloads for one chart or all charts on a sheet.

Returns chartXml + readId + excelDataSource + image.

Example:
[
  { "sheetName": "Dashboard" },
  { "sheetName": "Dashboard", "chartId": "Revenue Chart" }
]
`),
    listCharts: z.array(ListChartsItemSchema).optional().describe(`
List chart handles on a sheet (chartId/chartName/sheetName) without full chartXml payload.

Use this when you only need discovery and not authoritative chart XML.

Example:
[
  { "sheetName": "Dashboard" }
]
`),
    formulaTrace: z.array(FormulaTraceItemSchema).optional().describe(`
Trace formula dependencies for individual cells.

Shows precedents (inputs) and dependents (outputs) with values.
Only accepts single cells, not ranges.

Example:
[
  { "sheetName": "Model", "cellAddress": "B10", "direction": "both" }
]
`),
    tableAnalysis: z.array(TableAnalysisItemSchema).optional().describe(`
Statistical analysis of table data.

Returns for each column/row:
- Data type detection (string, number, date, mixed)
- Statistics (mean, median, stdev for numbers)
- ENUM detection (≤20 unique string values)
- Trend detection
- Empty cell patterns

Example:
[
  { "sheetName": "Customers", "targetRange": "A1:F100", "groupBy": "column" }
]
`),
    listSheets: z.array(ListSheetsItemSchema).optional().describe(`
List all sheet names in a workbook.

Use this to discover available sheets before reading content.
Returns sheet names, dimensions, and basic info (charts, tables, etc.).

Example:
[
  { "fileIndex": 0 }
]
`),
});
